<template>
  <div>
    <v-menu v-if="canAccess(access.FULL, accessLevel)" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          class="menu-dropdown text-black"
          variant="text"
          data-cy="devicePopoverDropdown"
        >
          <v-icon icon="mdi-dots-vertical" />
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          @click="openDialog('info')"
          link
          data-cy="deviceInfoActivator"
          prepend-icon="mdi-information"
          :title="$t('map.popover.menu.deviceInfo')"
        />
        <v-list-item
          @click="openDialog('settings')"
          link
          data-cy="deviceSettingsActivator"
          prepend-icon="mdi-cog-box"
          :title="$t('map.popover.menu.deviceSettings')"
        />
        <v-list-item
          @click="openDialog('alerts')"
          link
          data-cy="deviceAlertsActivator"
          prepend-icon="mdi-bell"
          :title="$t('map.popover.menu.deviceAlerts')"
        />
        <v-list-item
          @click="startMovePin"
          data-cy="movePin"
          prepend-icon="mdi-cursor-move"
          :title="$t('map.popover.menu.movePin')"
        />
        <v-list-item
          @click="openDialog('sharing')"
          link
          data-cy="deviceSharing"
          prepend-icon="mdi-share-variant"
          :title="$t('map.popover.menu.deviceSharing')"
        />
      </v-list>
    </v-menu>
    <DeviceInfoDialog :open="dialogs.info" :deviceId="deviceId" @close="closeDialog('info')" />
    <DeviceSettingsDialog
      :open="dialogs.settings"
      :deviceId="deviceId"
      :permissions="permissions"
      @close="closeDialog('settings')"
      @unregister="handleUnregister"
    />
    <DeviceAlertsDialog
      :open="dialogs.alerts"
      :deviceId="deviceId"
      :permissions="permissions"
      @close="closeDialog('alerts')"
    />
    <DeviceSharingDialog
      :open="dialogs.sharing"
      :deviceId="deviceId"
      :permissions="permissions"
      @close="closeDialog('sharing')"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useAccess } from '@/services/device-access'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { MAP_ACTIONS } from '@/store/map'
import DeviceInfoDialog from '@/components/dialogs/deviceInfo/DeviceInfoDialog.vue'
import DeviceSettingsDialog from '@/components/dialogs/DeviceSettingsDialog.vue'
import DeviceSharingDialog from '@/components/dialogs/DeviceSharingDialog.vue'
import DeviceAlertsDialog from '@/components/dialogs/DeviceAlertsDialog.vue'
import type { DeviceCoords } from '@/services/device-service/devices'

type DeviceDialogKey = 'info' | 'settings' | 'alerts' | 'sharing'

interface MovableDevice {
  deviceId: string
  coords: DeviceCoords
}

export default defineComponent({
  name: 'PopoverMenu',
  emits: ['unregister'],
  components: {
    DeviceInfoDialog,
    DeviceSettingsDialog,
    DeviceSharingDialog,
    DeviceAlertsDialog
  },
  setup() {
    const { access, canAccess } = useAccess()
    return {
      access,
      canAccess
    }
  },
  props: {
    deviceId: {
      type: String,
      default: ''
    },
    accessLevel: {
      type: Number,
      default: 0
    },
    permissions: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    }
  },
  data() {
    return {
      dialogs: {
        info: false,
        settings: false,
        alerts: false,
        sharing: false
      }
    }
  },
  methods: {
    openDialog(dialog: DeviceDialogKey): void {
      this.dialogs[dialog] = true
    },
    closeDialog(dialog: DeviceDialogKey): void {
      this.dialogs[dialog] = false
    },
    startMovePin(): void {
      const device: MovableDevice = {
        deviceId: this.deviceId,
        coords: this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`
        ](this.deviceId)
      }
      this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.START_MOVING}`, device)
    },
    handleUnregister(): void {
      this.$emit('unregister')
    }
  }
})
</script>

<style lang="scss" scoped>
.menu-dropdown {
  min-width: 10px !important;
  padding: 0 0.4rem !important;

  .v-icon {
    font-size: 1.5rem;
  }
}
</style>
